import { lazy, Suspense, type ReactElement } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { LanguageProvider } from "@/i18n/LanguageContext";
import LocalePathSync from "@/i18n/LocalePathSync";
import { localizePath, localeFromPath } from "@/i18n/localePath";
import { REDIRECT_ROUTES } from "@/seo/routes";
import AnalyticsListener from "./components/AnalyticsListener";
import SiteJsonLd from "./components/SiteJsonLd";
import Index from "./pages/Index";

// Code-split every non-home route so the homepage remains the smallest entry.
const Services = lazy(() => import("./pages/Services"));
const ProductionAISystems = lazy(() => import("./pages/ProductionAISystems"));
const DataPlatforms = lazy(() => import("./pages/DataPlatforms"));
const DecisionIntelligence = lazy(() => import("./pages/DecisionIntelligence"));
const AIVerificationReview = lazy(() => import("./pages/AIVerificationReview"));
const AIVerificationReviewApplication = lazy(() => import("./pages/AIVerificationReviewApplication"));
const SampleAIVerificationReview = lazy(() => import("./pages/SampleAIVerificationReview"));
const AIReadinessScorecard = lazy(() => import("./pages/AIReadinessScorecard"));
const Solutions = lazy(() => import("./pages/Solutions"));
const AIAgentsWorkflowAutomation = lazy(() => import("./pages/solutions/AIAgentsWorkflowAutomation"));
const DocumentIntelligence = lazy(() => import("./pages/solutions/DocumentIntelligence"));
const KnowledgeSystems = lazy(() => import("./pages/solutions/KnowledgeSystems"));
const CustomerSupportSystems = lazy(() => import("./pages/solutions/CustomerSupportSystems"));
const PredictiveModelsDecisionScience = lazy(() => import("./pages/solutions/PredictiveModelsDecisionScience"));
const Industries = lazy(() => import("./pages/Industries"));
const RetailEcommerceAI = lazy(() => import("./pages/industries/RetailEcommerceAI"));
const CpgAI = lazy(() => import("./pages/industries/CpgAI"));
const FranchiseMultiLocationAI = lazy(() => import("./pages/industries/FranchiseMultiLocationAI"));
const Work = lazy(() => import("./pages/Work"));
const About = lazy(() => import("./pages/About"));
const Insights = lazy(() => import("./pages/Insights"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const NotFound = lazy(() => import("./pages/NotFound"));

const routerFuture = {
  v7_relativeSplatPath: true,
  v7_startTransition: true,
} as const;

type PublicRoute = {
  path: string;
  element: ReactElement;
};

const publicRoutes: PublicRoute[] = [
  { path: "/", element: <Index /> },
  { path: "/services", element: <Services /> },
  { path: "/services/production-ai-systems", element: <ProductionAISystems /> },
  { path: "/services/ai-verification-review", element: <AIVerificationReview /> },
  { path: "/services/data-platforms", element: <DataPlatforms /> },
  { path: "/services/decision-intelligence", element: <DecisionIntelligence /> },
  { path: "/apply/ai-verification-review", element: <AIVerificationReviewApplication /> },
  { path: "/resources/sample-ai-verification-review", element: <SampleAIVerificationReview /> },
  { path: "/ai-readiness-scorecard", element: <AIReadinessScorecard /> },
  { path: "/solutions", element: <Solutions /> },
  { path: "/solutions/ai-agents-workflow-automation", element: <AIAgentsWorkflowAutomation /> },
  { path: "/solutions/document-intelligence", element: <DocumentIntelligence /> },
  { path: "/solutions/knowledge-systems-enterprise-retrieval", element: <KnowledgeSystems /> },
  { path: "/solutions/customer-support-service-systems", element: <CustomerSupportSystems /> },
  { path: "/solutions/predictive-models-decision-science", element: <PredictiveModelsDecisionScience /> },
  { path: "/industries", element: <Industries /> },
  { path: "/industries/retail-ecommerce-ai", element: <RetailEcommerceAI /> },
  { path: "/industries/cpg-ai", element: <CpgAI /> },
  { path: "/industries/franchise-multi-location-ai", element: <FranchiseMultiLocationAI /> },
  { path: "/work", element: <Work /> },
  { path: "/about", element: <About /> },
  { path: "/insights", element: <Insights /> },
  { path: "/insights/:slug", element: <ArticleDetail /> },
  { path: "/contact", element: <Contact /> },
  { path: "/privacy", element: <Privacy /> },
  { path: "/unsubscribe", element: <Unsubscribe /> },
];

const LocalizedRedirect = ({ to }: { to: string }) => {
  const { pathname } = useLocation();
  return <Navigate to={localizePath(to, localeFromPath(pathname))} replace />;
};

const AppRoutes = () => (
  <>
    <LocalePathSync />
    <AnalyticsListener />
    <SiteJsonLd />
    <Suspense fallback={null}>
      <Routes>
        {publicRoutes.flatMap((route) => ([
          <Route key={`en:${route.path}`} path={route.path} element={route.element} />,
          <Route key={`fr:${route.path}`} path={localizePath(route.path, "fr")} element={route.element} />,
        ]))}
        {REDIRECT_ROUTES.flatMap((route) => ([
          <Route key={`redirect:en:${route.from}`} path={route.from} element={<LocalizedRedirect to={route.to} />} />,
          <Route
            key={`redirect:fr:${route.from}`}
            path={localizePath(route.from, "fr")}
            element={<LocalizedRedirect to={route.to} />}
          />,
        ]))}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  </>
);

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
    <BrowserRouter future={routerFuture}>
      <LanguageProvider>
        <AppRoutes />
      </LanguageProvider>
    </BrowserRouter>
  </ThemeProvider>
);

export default App;
